import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { MOTION, NEUTRAL, SHADOW, SURFACE } from "../../../theme/tokens";

type Props = {
	/** Card body, laid out as a flex column (label, summary, actions, …). */
	children: ReactNode;
	/** Recedes the card to 0.6 opacity for a terminal/inactive state (rejected/expired). */
	dimmed?: boolean;
	/**
	 * Makes the WHOLE card the control — for a card whose only purpose is one action (an offer to be
	 * shown something). Adds hover lift, press-scale and a focus ring, all reduced-motion safe. Omit for
	 * a card that holds its own buttons; nesting a button inside a clickable card is invalid markup.
	 */
	onClick?: (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => void;
	/** Accessible name for the interactive variant — say what will happen, since the card acts on the app. */
	ariaLabel?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

// Mount fade + tiny lift (transform/opacity only); reduced-motion drops it.
const ENTRANCE_SX: SxProps<Theme> = {
	"@keyframes chatCardEnter": {
		from: { opacity: 0, transform: "translateY(4px)" },
		to: { opacity: 1, transform: "translateY(0)" },
	},
	animation: `chatCardEnter ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
	"@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

/**
 * The shared assistant-card shell for a chat surface: a leading-aligned,
 * full-width bordered panel on the app surface with a soft shadow and a
 * reduced-motion-safe mount fade. Wraps richer-than-a-bubble assistant content
 * (a proposal awaiting a decision, a disambiguation question); the caller owns
 * the inner layout. `dimmed` recedes the card for a terminal/inactive state.
 *
 * Pass `onClick` when the card IS the action — see that prop.
 */
export const ChatCard: FC<Props> = ({ children, dimmed = false, onClick, ariaLabel, dataTestId, sx }) => {
	const interactive = onClick !== undefined;

	// `role="button"` on a Box rather than a real <button>, matching `MetaChip`: an interactive card can
	// hold block content, which is invalid inside a button element.
	const handleKeyDown = interactive
		? (event: KeyboardEvent<HTMLElement>) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onClick?.(event);
				}
			}
		: undefined;

	return (
		<Box
			data-testid={dataTestId}
			onClick={onClick}
			onKeyDown={handleKeyDown}
			role={interactive ? "button" : undefined}
			tabIndex={interactive ? 0 : undefined}
			aria-label={interactive ? ariaLabel : undefined}
			sx={[
				ENTRANCE_SX,
				(theme) => ({
					alignSelf: "flex-start",
					width: "100%",
					display: "flex",
					flexDirection: "column",
					gap: 1.25,
					p: 1.75,
					border: `1px solid ${NEUTRAL[200]}`,
					borderRadius: 2,
					backgroundColor: SURFACE,
					boxShadow: SHADOW.PANEL_SOFT,
					...(dimmed && { opacity: 0.6 }),
					...(interactive && {
						cursor: "pointer",
						textAlign: "start",
						transition: theme.transitions.create(["box-shadow", "transform", "border-color"], { duration: MOTION.DURATION.HOVER }),
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { boxShadow: SHADOW.PANEL_SOFT_HOVER, transform: "translateY(-1px)" },
						},
						"&:active": { transform: "scale(0.99)" },
						"&:focus-visible": {
							outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
							outlineOffset: 2,
						},
						"@media (prefers-reduced-motion: reduce)": {
							transition: "none",
							"&:hover": { transform: "none" },
							"&:active": { transform: "none" },
						},
					}),
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{children}
		</Box>
	);
};
